import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const CookieBanner = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true,
    analytics: false,
    marketing: false,
    preferences: false
  });

  useEffect(() => {
    const cookieConsent = localStorage.getItem('cookieConsent');
    if (!cookieConsent) {
      setTimeout(() => setShowBanner(true), 500);
    } else {
      try {
        const saved = JSON.parse(cookieConsent);
        setPreferences(saved.preferences || preferences);
        applyCookiePreferences(saved.preferences);
      } catch (e) {
        setShowBanner(true);
      }
    }
   
  }, []);

  const applyCookiePreferences = (prefs) => {
    // Google Analytics 4 — attivo solo se utente ha consentito cookie analytici
    if (prefs.analytics) {
      if (typeof window !== 'undefined' && !window._ga4_loaded) {
        const GA_ID = 'G-VR8EWF4S6M';
        // Carica script gtag.js una sola volta
        const s = document.createElement('script');
        s.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
        s.async = true;
        document.head.appendChild(s);
        window.dataLayer = window.dataLayer || [];
        window.gtag = function () { window.dataLayer.push(arguments); };
        window.gtag('js', new Date());
        window.gtag('config', GA_ID, { anonymize_ip: true, cookie_expires: 68256000 });
        window._ga4_loaded = true;
      }
    } else if (window._ga4_loaded && window.gtag) {
      // Disabilita tracking se consenso revocato
      window[`ga-disable-G-VR8EWF4S6M`] = true;
    }
    if (prefs.marketing) { /* marketing enabled */ }
    if (prefs.preferences) { /* preferences enabled */ }
  };

  const saveConsent = (accepted, customPrefs = null) => {
    const finalPrefs = customPrefs || (accepted ? {
      necessary: true, analytics: true, marketing: true, preferences: true
    } : {
      necessary: true, analytics: false, marketing: false, preferences: false
    });

    localStorage.setItem('cookieConsent', JSON.stringify({
      accepted,
      preferences: finalPrefs,
      timestamp: new Date().toISOString(),
      version: '1.0'
    }));
    applyCookiePreferences(finalPrefs);
    setShowBanner(false);
    setShowSettings(false);
  };

  if (!showBanner) return null;

  return (
    <>
      {/* Subtle overlay */}
      <div className="fixed inset-0 bg-black/20 z-[9998] backdrop-blur-[1px]" />
      
      {/* Minimalist Cookie Banner */}
      <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-6 md:max-w-md z-[9999]" data-testid="cookie-banner">
        <div className="bg-white/95 backdrop-blur-sm rounded-xl shadow-lg border border-soft-cream/50 overflow-hidden">
          {!showSettings ? (
            <div className="p-4">
              {/* Compact Header */}
              <div className="flex items-center gap-2 mb-2">
                <span className="material-symbols-outlined text-primary text-lg">cookie</span>
                <span className="font-semibold text-charcoal text-sm">Utilizziamo i Cookie</span>
              </div>
              
              {/* Brief Text */}
              <p className="text-xs text-terra-siena leading-relaxed mb-3">
                Questo sito usa cookie per migliorare la tua esperienza.
                <Link to="/cookie-policy" className="ml-1 text-primary hover:underline">
                  Scopri di più
                </Link>
              </p>

              {/* Compact Buttons */}
              <div className="flex items-center gap-2">
                <button
                  onClick={() => saveConsent(true)}
                  className="flex-1 bg-primary hover:bg-primary/90 text-espresso text-xs font-semibold py-2 px-3 rounded-lg transition-all"
                  data-testid="cookie-accept-all"
                >
                  Accetta
                </button>
                <button
                  onClick={() => saveConsent(false)}
                  className="flex-1 bg-espresso/10 hover:bg-espresso/20 text-espresso text-xs font-semibold py-2 px-3 rounded-lg transition-all"
                  data-testid="cookie-reject-all"
                >
                  Rifiuta
                </button>
                <button
                  onClick={() => setShowSettings(true)}
                  className="p-2 text-terra-siena hover:text-primary hover:bg-soft-cream rounded-lg transition-all"
                  data-testid="cookie-customize"
                  title="Personalizza"
                >
                  <span className="material-symbols-outlined text-base">tune</span>
                </button>
              </div>
            </div>
          ) : (
            /* Settings Panel - Also Compact */
            <div className="p-4">
              {/* Header */}
              <div className="flex items-center gap-2 mb-3">
                <button 
                  onClick={() => setShowSettings(false)}
                  className="p-1 hover:bg-soft-cream rounded transition-all"
                >
                  <span className="material-symbols-outlined text-charcoal text-base">arrow_back</span>
                </button>
                <span className="font-semibold text-charcoal text-sm">Preferenze Cookie</span>
              </div>

              {/* Cookie Options */}
              <div className="space-y-2 mb-3">
                {/* Necessary */}
                <div className="flex items-center justify-between py-2 border-b border-soft-cream/50">
                  <div className="flex items-center gap-2">
                    <span className="material-symbols-outlined text-green-600 text-sm">lock</span>
                    <span className="text-xs font-medium text-charcoal">Necessari</span>
                  </div>
                  <span className="text-[10px] text-green-600 font-semibold">Sempre attivi</span>
                </div>

                {/* Analytics */}
                <div className="flex items-center justify-between py-2 border-b border-soft-cream/50">
                  <div className="flex items-center gap-2">
                    <span className="material-symbols-outlined text-blue-600 text-sm">analytics</span>
                    <span className="text-xs font-medium text-charcoal">Analitici</span>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={preferences.analytics}
                      onChange={(e) => setPreferences({...preferences, analytics: e.target.checked})}
                      className="sr-only peer"
                      data-testid="cookie-toggle-analytics"
                    />
                    <div className="w-8 h-4 bg-gray-200 rounded-full peer peer-checked:after:translate-x-4 peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-3 after:w-3 after:transition-all peer-checked:bg-primary"></div>
                  </label>
                </div>

                {/* Marketing */}
                <div className="flex items-center justify-between py-2 border-b border-soft-cream/50">
                  <div className="flex items-center gap-2">
                    <span className="material-symbols-outlined text-purple-600 text-sm">campaign</span>
                    <span className="text-xs font-medium text-charcoal">Marketing</span>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={preferences.marketing}
                      onChange={(e) => setPreferences({...preferences, marketing: e.target.checked})}
                      className="sr-only peer"
                      data-testid="cookie-toggle-marketing"
                    />
                    <div className="w-8 h-4 bg-gray-200 rounded-full peer peer-checked:after:translate-x-4 peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-3 after:w-3 after:transition-all peer-checked:bg-primary"></div>
                  </label>
                </div>

                {/* Preferences */}
                <div className="flex items-center justify-between py-2">
                  <div className="flex items-center gap-2">
                    <span className="material-symbols-outlined text-amber-600 text-sm">tune</span>
                    <span className="text-xs font-medium text-charcoal">Preferenze</span>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={preferences.preferences}
                      onChange={(e) => setPreferences({...preferences, preferences: e.target.checked})}
                      className="sr-only peer"
                      data-testid="cookie-toggle-preferences"
                    />
                    <div className="w-8 h-4 bg-gray-200 rounded-full peer peer-checked:after:translate-x-4 peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-3 after:w-3 after:transition-all peer-checked:bg-primary"></div>
                  </label>
                </div>
              </div>

              {/* Save Button */}
              <button
                onClick={() => saveConsent(true, preferences)}
                className="w-full bg-primary hover:bg-primary/90 text-espresso text-xs font-semibold py-2 px-3 rounded-lg transition-all"
                data-testid="cookie-save-preferences"
              >
                Salva preferenze
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default CookieBanner;
